import { useMedplum } from '@medplum/react-hooks';
import type { Practitioner, Extension } from '@medplum/fhirtypes';
import { Checkbox, Divider, Group, Radio, SimpleGrid, Stack, Text, TextInput, Title } from '@mantine/core';
import { notify } from '@/lib/notify';
import { useForm } from '@mantine/form';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  EXT_URLS,
  GENDER_IDENTITY_EXT,
  LGBTQIA_IDENTITIES_EXT,
  RACE_ETHNICITY_EXT,
  RELIGION_SPIRITUALITY_EXT,
} from '@unify/fhir-model/constants';
import {
  getClinical,
  getPractitionerLgbtqiaIdentities,
  getPractitionerGenderIdentity,
  getPractitionerPronouns,
  getPractitionerRaceEthnicity,
  getPractitionerReligionSpirituality,
  setClinical,
} from '@unify/fhir-model/resources/practitioner';
import { profileFieldLabels, profileSchema, type ProfileFormValues } from '@/schemas/practitioner.schema';
import { OnboardingSection } from '../OnboardingSection';
import {
  AGE_GROUP_OPTIONS,
  CLINICAL_SPECIALTIES_OPTIONS,
  GENDER_IDENTITY_OPTIONS,
  LGBTQIA_OPTIONS,
  RACE_ETHNICITY_OPTIONS,
  RELIGION_OPTIONS,
  TREATMENT_MODALITIES_OPTIONS,
} from '../constants';
import { useOnboardingPractitioner } from '../useOnboardingPractitioner';
import { completeProviderOnboardingSection } from '../../../lib/providerOnboardingService';
import { missingFieldMessage } from '../../../lib/formValidation';

function parseJsonArray(raw?: string): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === 'string') : [];
  } catch {
    return raw ? [raw] : [];
  }
}

function upsertStringExtension(resource: Practitioner, url: string, value: string): Practitioner {
  const extensions = (resource.extension ?? []).filter((extension) => extension.url !== url);
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return { ...resource, extension: extensions };
  }

  return {
    ...resource,
    extension: [...extensions, { url, valueString: trimmedValue } satisfies Extension],
  };
}

function upsertJsonStringExtension(resource: Practitioner, url: string, values: string[]): Practitioner {
  const extensions = (resource.extension ?? []).filter((extension) => extension.url !== url);
  if (values.length === 0) {
    return { ...resource, extension: extensions };
  }

  return {
    ...resource,
    extension: [...extensions, { url, valueString: JSON.stringify(values) } satisfies Extension],
  };
}

export function ProfileSection(): JSX.Element {
  const medplum = useMedplum();
  const profile = useOnboardingPractitioner();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const hydratedProfileVersionRef = useRef<string | undefined>(undefined);
  const form = useForm<ProfileFormValues>({
    initialValues: {
      specialties: [],
      modalities: [],
      ageGroups: [],
      gender: '',
      lgbtqia: '',
      religion: [],
      pronouns: '',
      raceEthnicity: [],
    },
    validate: zod4Resolver(profileSchema),
  });

  const formRef = useRef(form);
  formRef.current = form;

  useEffect(() => {
    if (!profile || profile.resourceType !== 'Practitioner') {
      return;
    }

    const hydrationKey = `${profile.id ?? 'unknown'}:${profile.meta?.versionId ?? profile.meta?.lastUpdated ?? 'initial'}`;
    if (hydratedProfileVersionRef.current === hydrationKey) {
      return;
    }
    hydratedProfileVersionRef.current = hydrationKey;

    const clinical = getClinical(profile);
    formRef.current.setValues({
      specialties: clinical?.specialties ?? [],
      modalities: clinical?.modalities ?? [],
      ageGroups: clinical?.ageGroups ?? [],
      gender: getPractitionerGenderIdentity(profile) ?? '',
      lgbtqia: getPractitionerLgbtqiaIdentities(profile) ?? '',
      religion: parseJsonArray(getPractitionerReligionSpirituality(profile)),
      pronouns: getPractitionerPronouns(profile) ?? '',
      raceEthnicity: parseJsonArray(getPractitionerRaceEthnicity(profile)),
    });
  }, [profile]);

  const handleSave = form.onSubmit(
    async (values) => {
      if (!profile || profile.resourceType !== 'Practitioner') {
        notify({
          type: 'error',
          title: 'Could not save profile',
          message: 'Provider profile is not loaded yet. Please try again.',
        });
        return;
      }

      setSaving(true);
      try {
        let updated = setClinical(profile, {
          specialties: values.specialties,
          modalities: values.modalities,
          ageGroups: values.ageGroups,
        });

        updated = upsertStringExtension(updated, GENDER_IDENTITY_EXT, values.gender);
        updated = upsertStringExtension(updated, LGBTQIA_IDENTITIES_EXT, values.lgbtqia);
        updated = upsertJsonStringExtension(updated, RELIGION_SPIRITUALITY_EXT, values.religion);
        updated = upsertStringExtension(updated, EXT_URLS.pronouns, values.pronouns);
        updated = upsertJsonStringExtension(updated, RACE_ETHNICITY_EXT, values.raceEthnicity);

        await medplum.updateResource(updated);
        const sectionResult = await completeProviderOnboardingSection(medplum, 'profile');
        if (!sectionResult.ok) {
          const detail = sectionResult.missingSections
            .map((s) => `${s.title}: ${s.missingFields.join(', ')}`)
            .join('; ');
          notify({
            type: 'error',
            title: 'Could not verify onboarding',
            // eslint-disable-next-line no-restricted-syntax -- bot-authored static text (provider-onboarding-completion bot), not exception text
            message: detail || sectionResult.message,
          });
          return;
        }

        medplum.invalidateSearches('Practitioner');
        medplum.invalidateSearches('Task');
        notify({
          type: 'success',
          title: 'Profile saved',
          message: 'Your profile has been updated successfully.',
        });
        navigate('/onboarding');
      } catch (error) {
        notify({
          type: 'error',
          title: 'Could not save profile',
          error: error,
          message: 'Please review required fields and try again.',
        });
      } finally {
        setSaving(false);
      }
    },
    () => {
      notify({
        type: 'error',
        title: 'Missing required fields',
        message: missingFieldMessage(Object.keys(form.errors), profileFieldLabels),
      });
    },
  );

  return (
    <OnboardingSection
      title="Profile"
      onSave={() => handleSave()}
      saveLabel="Save"
      isSaving={saving}
    >
      <Stack gap="xl">
        {/* Clinical specialties (required) */}
        <div>
          <Title order={5} mb="xs" c="dark">
            Clinical specialties <Text component="span" c="red" ml={4}>*</Text>
          </Title>
          <Checkbox.Group key={form.key('specialties')} {...form.getInputProps('specialties')}>
            <SimpleGrid cols={3} spacing="xs" verticalSpacing="xs">
              {CLINICAL_SPECIALTIES_OPTIONS.map((opt) => (
                <Checkbox key={opt} value={opt} label={opt} />
              ))}
            </SimpleGrid>
          </Checkbox.Group>
          {form.errors.specialties && (
            <Text size="xs" c="red" mt="xs">{form.errors.specialties}</Text>
          )}
        </div>

        {/* Treatment modalities (required) */}
        <div>
          <Title order={5} mb="xs" c="dark">
            Treatment modalities <Text component="span" c="red" ml={4}>*</Text>
          </Title>
          <Checkbox.Group key={form.key('modalities')} {...form.getInputProps('modalities')}>
            <SimpleGrid cols={2} spacing="xs" verticalSpacing="xs">
              {TREATMENT_MODALITIES_OPTIONS.map((opt) => (
                <Checkbox key={opt} value={opt} label={opt} />
              ))}
            </SimpleGrid>
          </Checkbox.Group>
          {form.errors.modalities && (
            <Text size="xs" c="red" mt="xs">{form.errors.modalities}</Text>
          )}
        </div>

        {/* Preferred patient age groups (required) */}
        <div>
          <Title order={5} mb="xs" c="dark">
            Preferred patient age groups <Text component="span" c="red" ml={4}>*</Text>
          </Title>
          <Checkbox.Group key={form.key('ageGroups')} {...form.getInputProps('ageGroups')}>
            <SimpleGrid cols={2} spacing="xs" verticalSpacing="xs">
              {AGE_GROUP_OPTIONS.map((opt) => (
                <Checkbox key={opt} value={opt} label={opt} />
              ))}
            </SimpleGrid>
          </Checkbox.Group>
          {form.errors.ageGroups && (
            <Text size="xs" c="red" mt="xs">{form.errors.ageGroups}</Text>
          )}
        </div>

        <Divider />

        <div>
          <Text size="md" fw={600} c="gray.8" mb={4}>Demographics</Text>
          <Text size="sm" c="dimmed" mb="md">
            This information helps us match clients with providers who share or understand their background. All fields
            are optional.
          </Text>
        </div>

        {/* Gender identity */}
        <Radio.Group
          label="Gender identity"
          key={form.key('gender')}
          {...form.getInputProps('gender')}
        >
          <Group gap="lg" mt="xs">
            {GENDER_IDENTITY_OPTIONS.map((opt) => (
              <Radio key={opt} value={opt} label={opt} />
            ))}
          </Group>
        </Radio.Group>

        {/* Sexual identity / LGBTQIA+ */}
        <Radio.Group
          label="Sexual identity / LGBTQIA+"
          key={form.key('lgbtqia')}
          {...form.getInputProps('lgbtqia')}
        >
          <Group gap="lg" mt="xs">
            {LGBTQIA_OPTIONS.map((opt) => (
              <Radio key={opt} value={opt} label={opt} />
            ))}
          </Group>
        </Radio.Group>

        {/* Religion and spirituality */}
        <div>
          <Title order={5} mb="xs" c="dark">Religion and spirituality</Title>
          <Checkbox.Group key={form.key('religion')} {...form.getInputProps('religion')}>
            <SimpleGrid cols={3} spacing="xs" verticalSpacing="xs">
              {RELIGION_OPTIONS.map((opt) => (
                <Checkbox key={opt} value={opt} label={opt} />
              ))}
            </SimpleGrid>
          </Checkbox.Group>
        </div>

        {/* Pronouns */}
        <TextInput
          label="Pronouns"
          placeholder="e.g. she/her, he/him, they/them"
          key={form.key('pronouns')}
          {...form.getInputProps('pronouns')}
        />

        {/* Race / Ethnicity */}
        <div>
          <Title order={5} mb="xs" c="dark">Race / Ethnicity</Title>
          <Checkbox.Group key={form.key('raceEthnicity')} {...form.getInputProps('raceEthnicity')}>
            <SimpleGrid cols={3} spacing="xs" verticalSpacing="xs">
              {RACE_ETHNICITY_OPTIONS.map((opt) => (
                <Checkbox key={opt} value={opt} label={opt} />
              ))}
            </SimpleGrid>
          </Checkbox.Group>
        </div>
      </Stack>
    </OnboardingSection>
  );
}
